import type { WorkOrderItem } from '#/graphql/types';
import type { ColumnProps } from '#/common/components/DataGrid';
import { withChip, withDot } from '#/common/utils/wrappers';
import { color } from '@nuvolo/nuux-theme/utils';
import { Key } from '#/types';
import { prioritiesList, priorities } from '../../mappings/mappings';
import { gql } from '@apollo/client';
import { getBuildingList, getFsmWorkOrderTenants, getTenantList } from '#/graphql/queries';
import { hourLabel, priorityColorMap, workOrderStateColors } from './columns';

// Show the raw DB value as-is; look up colors by lowercase key.
export const renderFsmStateChip = (rawState?: string) => {
  if (!rawState) return null;
  const customColors = workOrderStateColors[rawState.toLowerCase()];
  return withChip(rawState, 'normal', customColors);
};

const fsmColumns: ColumnProps<WorkOrderItem, Key<WorkOrderItem>>[] = [
  { dataField: 'id', caption: 'ID', visible: false, showInColumnChooser: false },

  {
    dataField: 'type',
    caption: 'Type',
    dataType: 'string',
    showInColumnChooser: false,
  },
  {
    dataField: 'asset.serialNumber',
    caption: 'Asset',
    openOnClick: true,
    path: (row) => `/assets/${row.asset?.id}`,
    dataType: 'string',
    // Child-only: each WOLI has its own asset. Parent WO has no single asset to
    // sort/filter/search by in the grouped view. asset.serialNumber is not in
    // fsmGroupedFieldMap so any filter or search-bar query would cause a 500.
    showInColumnChooser: false,
    allowSorting: false,
    allowFiltering: false,
    allowSearch: false,
  },
  {
    dataField: 'assetId',
    caption: 'AssetId',
    visible: false,
    dataType: 'string',
    showInColumnChooser: false,
  },
  {
    dataField: 'priority',
    caption: 'Priority',
    dataType: 'number',
    lookup: {
      nullable: true,
      dataSource: prioritiesList,
      displayExpr: 'label',
      valueExpr: 'value',
    },
    cellRender: (e) => {
      if (e.value === null || e.value === undefined) {
        return null;
      }
      // Find the priority based on the numeric value
      const priority = Object.values(priorities).find((p) => p.value === e.value);
      if (!priority) return e.displayValue;

      return withDot(e.displayValue, priorityColorMap[priority.key] || color('ui', 'dark'));
    },
  },
  {
    dataField: 'state',
    caption: 'Status',
    dataType: 'string',
    cellRender: (e) => {
      const groupSummaryState =
        (e as any)?.rowType === 'group' ? (e as any)?.data?.summary?.[2] : undefined;

      let rawState: string | undefined;
      if (typeof e.value === 'string') {
        rawState = e.value;
      } else if (typeof groupSummaryState === 'string') {
        rawState = groupSummaryState;
      } else if (typeof e.displayValue === 'string') {
        rawState = e.displayValue;
      }

      return renderFsmStateChip(rawState);
    },
    groupCellRender: (e) => {
      let rawState: string | undefined;
      if (typeof e?.value === 'string') {
        rawState = e.value;
      } else if (typeof e?.data?.summary?.[2] === 'string') {
        rawState = e.data.summary[2];
      } else if (typeof e?.displayValue === 'string') {
        rawState = e.displayValue;
      }

      return renderFsmStateChip(rawState);
    },
  },
  {
    dataField: 'assetLocation.id',
    caption: 'Location',
    visible: true,
    dataType: 'string',
    filterField: 'assetLocation.name',
    calcFilterExpression: (value: unknown, operation: string) => [
      ['assetLocation.name', operation, value],
      'OR',
      ['location.name', operation, value],
    ],
    headerFilter: {
      getItemList: gql(getBuildingList),
      displayExpr: 'name',
    },
    cellRender: (e) => e.data?.assetLocation?.name ?? e.data?.location?.name ?? '',
  },
  {
    dataField: 'dueDate',
    caption: 'Planned Date',
    visible: true,
    dataType: 'date',
  },
  {
    dataField: 'tenant.name',
    caption: 'Organization',
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getTenantList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'startDate',
    caption: 'Start Date',
    visible: false,
    dataType: 'datetime',
  },
  {
    dataField: 'endDate',
    caption: 'End Date',
    visible: false,
    dataType: 'datetime',
  },
  {
    dataField: 'duration',
    caption: 'Duration',
    visible: false,
    dataType: 'string',
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'plannedStartDate',
    caption: 'Planned Start Date',
    visible: false,
    dataType: 'datetime',
    showInColumnChooser: false,
  },
  {
    dataField: 'plannedEndDate',
    caption: 'Planned End Date',
    visible: false,
    dataType: 'datetime',
    showInColumnChooser: false,
  },
  {
    dataField: 'assignmentGroupId',
    caption: 'Assignment Group ID',
    visible: false,
    dataType: 'uuid',
    showInColumnChooser: false,
  },
  {
    dataField: 'assignedToId',
    caption: 'Assigned To ID',
    visible: false,
    dataType: 'uuid',
    showInColumnChooser: false,
  },
  {
    dataField: 'assignedTo.name',
    caption: 'Assigned To',
    visible: false,
    dataType: 'string',
    // The grouped query does not join users for assigned-to. Filtering would
    // generate work_orders.name (wrong column) → 500.
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'businessDownTime',
    caption: 'Business Down Time',
    visible: false,
    showInColumnChooser: false,
    dataType: 'number',
  },
  {
    dataField: 'downTimeStart',
    caption: 'Down Time Start',
    visible: false,
    showInColumnChooser: false,
    dataType: 'datetime',
  },
  {
    dataField: 'downTimeEnd',
    caption: 'Down Time End',
    visible: false,
    showInColumnChooser: false,
    dataType: 'datetime',
  },
  {
    dataField: 'category',
    caption: 'Category',
    visible: false,
    dataType: 'string',
  },
  {
    dataField: 'checklist',
    caption: 'Checklist',
    visible: false,
    dataType: 'string',
    showInColumnChooser: false,
  },
  {
    dataField: 'closedNotes',
    caption: 'Closing Notes',
    visible: false,
    dataType: 'string',
  },
  {
    dataField: 'closedAt',
    caption: 'Closed At',
    visible: false,
    dataType: 'datetime',
    showInColumnChooser: false,
  },
  {
    dataField: 'closedBy.name',
    caption: 'Closed By',
    visible: false,
    dataType: 'string',
    // The grouped query does not join users for closed-by. Filtering would
    // generate work_orders.name (wrong column) → 500.
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'notes',
    caption: 'Notes',
    visible: false,
    dataType: 'string',
    showInColumnChooser: false,
  },
  {
    dataField: 'description',
    caption: 'Subject',
    visible: false,
    dataType: 'string',
  },
  {
    dataField: 'estimatedCostValue',
    caption: 'Estimated Cost',
    visible: false,
    dataType: 'number',
    allowFiltering: true,
    allowSearch: true,
    allowSorting: true,
    showInColumnChooser: false,
  },
  {
    dataField: 'actualCostValue',
    caption: 'Actual Cost',
    visible: false,
    dataType: 'number',
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'estimatedEffort',
    caption: 'Estimated Effort',
    visible: false,
    dataType: 'number',
    showInColumnChooser: false,
    calcDisplayValue: (row) =>
      !!row.estimatedEffort && row.estimatedEffort > 0 ? hourLabel(row.estimatedEffort) : '',
  },
  {
    dataField: 'actualEffort',
    caption: 'Actual Effort',
    visible: false,
    dataType: 'number',
    showInColumnChooser: false,
    calcDisplayValue: (row) =>
      !!row.actualEffort && row.actualEffort > 0 ? hourLabel(row.actualEffort) : '',
  },
  {
    dataField: 'expectedOperationalDate',
    caption: 'Expected Operational Date',
    visible: false,
    dataType: 'date',
    showInColumnChooser: false,
  },
  {
    dataField: 'impact',
    caption: 'Impact',
    visible: false,
    dataType: 'string',
    showInColumnChooser: false,
  },
  {
    dataField: 'isArchived',
    caption: 'Archived',
    dataType: 'boolean',
    showInColumnChooser: false,
  },
  {
    dataField: 'seenByAssignee',
    caption: 'Seen By Assignee',
    dataType: 'boolean',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'assignmentDateTime',
    caption: 'Assignment Date Time',
    dataType: 'datetime',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'seenDateTime',
    caption: 'Seen Date Time',
    dataType: 'datetime',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'startOfWorkDateTime',
    caption: 'Start Of Work Date Time',
    dataType: 'datetime',
    visible: false,
    showInColumnChooser: false,
  },
  {
    dataField: 'endOfWorkDateTime',
    caption: 'End Of Work Date Time',
    dataType: 'datetime',
    visible: false,
    showInColumnChooser: false,
  },
  {
    // In the parent grid this is a no-op (parent WOs have no parent).
    // In the child detail grid the parent is already the expanded row above — redundant.
    // In the flat WOLI grid this is visible and shows the parent WO number.
    dataField: 'parent.thirdPartyNumber',
    caption: 'Visit Number',
    visible: false,
    dataType: 'string',
    openOnClick: true,
    path: (row) => `/work-orders/${row.parent?.id || row.id}`,
    calcDisplayValue: (row) => {
      return (
        row?.parent?.thirdPartyNumber ||
        row?.parent?.displayNumber ||
        row?.thirdPartyNumber ||
        row?.displayNumber ||
        ''
      );
    },
  },
  {
    // Combined column: "Parent Number — Parent Title" for easy identification.
    dataField: 'parent.title',
    caption: 'Visit Title',
    visible: false,
    dataType: 'string',
    openOnClick: true,
    path: (row) => `/work-orders/${row.parent?.id || row.id}`,
    calcDisplayValue: (row) => {
      return row?.parent?.title || row?.title || '';
    },
  },
  {
    dataField: 'parent.description',
    caption: 'Description',
    visible: false,
    dataType: 'string',
    calcFilterExpression: (value: unknown, operation: string) => [
      ['parent.description', operation, value],
      'OR',
      ['description', operation, value],
    ],
    allowFiltering: false,
    allowSorting: false,
    allowSearch: false,
    cellRender: (e) => e.data?.parent?.description ?? e.data?.description ?? '',
  },

  {
    dataField: 'title',
    caption: 'WOLI Title',
    openOnClick: true,
    path: (row) => `/work-orders/${row.id}`,
    calcDisplayValue: (row) => (row.isParent === true && !row.parentId ? '' : (row.title ?? '')),
  },
  {
    dataField: 'displayNumber',
    caption: 'WOLI Number',
    // fixed: true — disabled in grouped grid (TMM-1558: blank group-row rendering).
    openOnClick: true,
    dataType: 'string',
    // Display shows thirdPartyNumber when present, otherwise displayNumber.
    // Filtering must mirror the same logic:
    //   - When thirdPartyNumber IS NOT NULL → match against thirdPartyNumber
    //   - When thirdPartyNumber IS NULL     → match against displayNumber
    // A plain filterField cannot express this, so calcFilterExpression emits a
    // composite OR expression that is fully serialisable for remote operations.
    calcFilterExpression: (value, operation) => {
      return [
        [['thirdPartyNumber', 'isNotNull'], 'AND', ['thirdPartyNumber', operation, value]],
        'OR',
        [['thirdPartyNumber', 'isNull'], 'AND', ['displayNumber', operation, value]],
      ];
    },
    sortIndex: 0,
    sortOrder: 'desc',
    calcDisplayValue: (row) => {
      if (row.isParent === true && !row.parentId) {
        return '';
      }

      const number = row.thirdPartyNumber || row.displayNumber || '';
      const count = Number(row.lineItemsCount ?? 0);

      if (count > 0) {
        return `${number} (${count})`;
      }

      return number;
    },
  },
];

// FSM Parent Work Orders — default visible columns for the parent (isParent = true) grid
export const fsmParentVisibleColumns = [
  'displayNumber',
  'title',
  'state',
  'assetLocation.id',
  'dueDate',
];

// FSM Line Items — default visible columns for the flat WOLI (child) grid
export const fsmLineItemsVisibleColumns = [
  'parent.thirdPartyNumber',
  'parent.title',
  'title',
  'displayNumber',
  'state',
  'assetLocation.id',
  'dueDate',
];

/**
 * Columns for the flat FSM WOLI grid.
 * parent.displayNumber and parent.title become fixed + visible + shown in column chooser.
 * displayNumber also becomes fixed (safe in flat grid — no group rows, so no TMM-1558).
 */
export const fsmFlatColumns = fsmColumns.map((col) => {
  if (col.dataField === 'parent.thirdPartyNumber' || col.dataField === 'parent.title') {
    return { ...col, fixed: true, showInColumnChooser: true };
  }
  if (col.dataField === 'displayNumber') {
    return { ...col, fixed: true, allowHiding: false, showInColumnChooser: false };
  }
  if (col.dataField === 'title') {
    return { ...col, fixed: true };
  }
  // Organization: expose in column chooser for the flat grid.
  if (col.dataField === 'tenant.name') {
    return {
      ...col,
      showInColumnChooser: true,
      allowFiltering: true,
      allowSearch: true,
      allowSorting: true,
      headerFilter: {
        getItemList: gql(getFsmWorkOrderTenants),
        displayExpr: 'name',
      },
    };
  }
  return col;
});

export default fsmColumns;
